import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormRenderer } from "@/components/forms/FormRenderer";
import { DuplicatePersonResolver } from "@/components/recruiting/DuplicatePersonResolver";
import { SourceSelect } from "@/components/common/SourceSelect";
import { UserPlus, CheckCircle2, Info, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LEAD_MAP = {
  first_name: "first_name", last_name: "last_name", email: "email", mobile: "mobile",
  city: "city", state: "state", source: "source", notes: "notes", comments: "notes", interested_in: "interest",
};

// "Interested In" expresses interest only — it never forces a final program classification (CRM rules decide).
const INTEREST_OPTIONS = [
  { code: "START_FROM_SCRATCH", label: "Start an Agency from Scratch" },
  { code: "BUY_EXISTING", label: "Buy an Existing Agency" },
  { code: "ASSOCIATE", label: "Associate Agent" },
  { code: "PROTEGE", label: "Protégé / Agency Producer" },
  { code: "WORK_IN_AGENCY", label: "Work in an Agency" },
  { code: "UNSURE", label: "Not Sure — I'd Like to Discuss My Options" },
];
const EMPTY = { first_name: "", last_name: "", email: "", mobile: "", city: "", state: "",
  interested_in: "", source: "", source_id: "", organization_id: "", assigned_recruiter_id: "", notes: "" };

export function AddLeadDialog({ open, onClose, onCreated }) {
  const { isReferralPartner } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (open && isReferralPartner) { onClose?.(); navigate("/referrals/add"); }
  }, [open, isReferralPartner]); // eslint-disable-line react-hooks/exhaustive-deps
  return isReferralPartner
    ? null
    : <InternalAddLead open={open} onClose={onClose} onCreated={onCreated} />;
}

export const myGroups = (ctx, account) => [...new Map((ctx?.memberships || [])
  .filter((m) => m.client_account_id === account && m.organization_id)
  .map((m) => [m.organization_id, { id: m.organization_id, name: m.organization_name || "Group" }])).values()];

function InternalAddLead({ open, onClose, onCreated }) {
  const { user, isOutside, ctx, activeAccount } = useAuth();
  const me = user || {};
  const navigate = useNavigate();
  const [v, setV] = useState({ ...EMPTY });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [srcWarn, setSrcWarn] = useState("");
  const defaultOrg = (list) => { const d = localStorage.getItem("tr_district") || ""; return list.some((o) => o.id === d) ? d : (list.length === 1 ? list[0].id : ""); };

  useEffect(() => {
    if (!open) return;
    setResult(null); setSrcWarn("");
    setV({ ...EMPTY, assigned_recruiter_id: me.id || "" });
    const applyList = (list) => { setOrgs(list); setV((s) => ({ ...s, organization_id: s.organization_id || defaultOrg(list) })); };
    // Outside Recruiters can't list Groups; offer only the Groups they belong to (server re-checks).
    if (isOutside) applyList(myGroups(ctx, activeAccount));
    else api.get("/organizations").then((r) => applyList(r.data.organizations || [])).catch(() => setOrgs([]));
  }, [open]);

  const set = (k) => (val) => setV((s) => ({ ...s, [k]: val }));
  const setOrg = (id) => { setSrcWarn(""); setV((s) => ({ ...s, organization_id: id })); };
  const onSourceUnavailable = useCallback((lbl) => {
    setSrcWarn(`“${lbl}” isn't available for this organization. Choose another source.`);
    setV((s) => ({ ...s, source: "", source_id: "" }));
  }, []);
  const reset = () => { setResult(null); setSrcWarn(""); setV({ ...EMPTY, organization_id: defaultOrg(orgs), assigned_recruiter_id: me.id || "" }); };
  const close = () => { reset(); onClose?.(); };
  const openPerson = (pid) => { close(); navigate(`/people/detail/${pid}`); };

  const recruiterChoices = me.id ? [{ id: me.id, name: `${me.name || "Me"} (me)` }] : [];

  const submit = async (e) => {
    e.preventDefault();
    if (!v.first_name.trim() || !v.last_name.trim()) return toast.error("First and last name are required.");
    if (!v.email.trim()) return toast.error("Email is required.");
    // Outside Recruiters can't list Groups (deny-by-default); the server resolves their own Group.
    if (!v.organization_id && (orgs.length > 1 || !isOutside)) return toast.error("Choose the destination organization.");
    setBusy(true);
    try {
      const { data } = await api.post("/leads", {
        first_name: v.first_name, last_name: v.last_name, email: v.email, mobile: v.mobile,
        city: v.city, state: v.state, interested_in: v.interested_in, source: v.source, source_id: v.source_id || undefined,
        organization_id: v.organization_id,
        assigned_recruiter_id: v.assigned_recruiter_id || undefined, notes: v.notes,
      });
      setResult(data);
      if (data.status === "created") { toast.success("Lead added"); onCreated?.(data); }
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const created = result?.status === "created";
  return (
    <Dialog open={open} onOpenChange={(x) => !x && close()}>
      <DialogContent className="bg-white max-w-lg max-h-[88vh] overflow-y-auto" data-testid="add-lead-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-brand" />Add Lead</DialogTitle>
          <DialogDescription>Manually add a recruiting lead. We check for an existing record before creating anyone new.</DialogDescription>
        </DialogHeader>

        {result ? (
          created ? (
            <div className="py-2 text-center" data-testid="add-lead-dialog-result">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
              <p className="font-display font-bold text-slate-900" data-testid="add-lead-dialog-created">Lead Added</p>
              <p className="text-slate-600 mt-0.5">{result.name}</p>
              <div className="flex justify-center gap-2 mt-4">
                {result.person_id && <Button variant="outline" onClick={() => openPerson(result.person_id)} data-testid="add-lead-dialog-open">Open Record</Button>}
                <Button className="bg-brand hover:bg-brand-strong text-white" onClick={reset} data-testid="add-lead-dialog-another"><UserPlus className="w-4 h-4 mr-1.5" />Add Another</Button>
              </div>
            </div>
          ) : result.scope_class ? (
            <div data-testid="add-lead-dialog-result">
              <DuplicatePersonResolver result={result} values={v} onReset={reset} onClose={close} onCreated={onCreated} setResult={setResult} />
            </div>
          ) : (
            <div className="py-2 text-center" data-testid="add-lead-dialog-result">
              <Info className="w-10 h-10 text-slate-400 mx-auto mb-2" />
              <p className="font-display font-bold text-slate-900">Already in Top-Recruit</p>
              <p className="text-sm text-slate-600 mt-1" data-testid="add-lead-dialog-existing">{result.message}</p>
              <div className="flex justify-center gap-2 mt-4">
                {result.allow_open && result.person_id && <Button variant="outline" onClick={() => openPerson(result.person_id)} data-testid="add-lead-dialog-open-existing"><ExternalLink className="w-4 h-4 mr-1.5" />Open {result.name || "Person"}</Button>}
                <Button className="bg-brand hover:bg-brand-strong text-white" onClick={reset} data-testid="add-lead-dialog-another">Add Another</Button>
              </div>
            </div>
          )
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Fld label="First Name*"><Input value={v.first_name} onChange={(e) => set("first_name")(e.target.value)} data-testid="lead-first" /></Fld>
              <Fld label="Last Name*"><Input value={v.last_name} onChange={(e) => set("last_name")(e.target.value)} data-testid="lead-last" /></Fld>
              <Fld label="Email*"><Input type="email" value={v.email} onChange={(e) => set("email")(e.target.value)} data-testid="lead-email" /></Fld>
              <Fld label="Mobile Phone"><Input value={v.mobile} onChange={(e) => set("mobile")(e.target.value)} data-testid="lead-mobile" /></Fld>
              <Fld label="City"><Input value={v.city} onChange={(e) => set("city")(e.target.value)} data-testid="lead-city" /></Fld>
              <Fld label="State"><Input value={v.state} onChange={(e) => set("state")(e.target.value)} data-testid="lead-state" /></Fld>
            </div>
            <Fld label="Interested In">
              <Select value={v.interested_in} onValueChange={set("interested_in")}>
                <SelectTrigger data-testid="lead-interested"><SelectValue placeholder="Select an option (optional)" /></SelectTrigger>
                <SelectContent className="bg-white">{INTEREST_OPTIONS.map((o) => <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </Fld>
            {orgs.length > 1 && (
              <Fld label="Destination Organization*">
                <Select value={v.organization_id} onValueChange={setOrg}>
                  <SelectTrigger data-testid="lead-org"><SelectValue placeholder="Choose organization" /></SelectTrigger>
                  <SelectContent className="bg-white">{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Fld label="Source">
                <SourceSelect organizationId={v.organization_id} value={v.source_id} label={v.source} testId="lead-source"
                  onChange={({ source_id, source }) => { setSrcWarn(""); setV((s) => ({ ...s, source_id, source })); }}
                  onUnavailable={onSourceUnavailable} />
                {srcWarn && <p className="text-xs text-amber-700 mt-1" role="alert" data-testid="lead-source-warning">{srcWarn}</p>}
              </Fld>
              <Fld label="Assigned Recruiter">
                <Select value={v.assigned_recruiter_id} onValueChange={set("assigned_recruiter_id")}>
                  <SelectTrigger data-testid="lead-recruiter"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent className="bg-white">{recruiterChoices.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
            </div>
            <Fld label="Notes"><Textarea value={v.notes} onChange={(e) => set("notes")(e.target.value)} rows={2} data-testid="lead-notes" placeholder="Context about this lead (optional)" /></Fld>
            <p className="text-xs text-slate-400">We'll check for an existing record before creating anyone new. Adding a lead does not create a Candidate Portal account.</p>
            <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white">
              <Button type="button" variant="outline" onClick={close} data-testid="add-lead-dialog-cancel">Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-brand hover:bg-brand-strong text-white" data-testid="add-lead-dialog-submit"><UserPlus className="w-4 h-4 mr-1.5" />{busy ? "Adding…" : "Add Lead"}</Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Fld({ label, children }) {
  return <div><Label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</Label>{children}</div>;
}

// Referral Partner path: keep the authoritative published form + dedicated attributed endpoint.
function ReferralPartnerAddLead({ open, onClose, onCreated }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setResult(null); setValues({});
    api.get("/forms/active/add_lead")
      .then(({ data }) => setForm(data))
      .catch((e) => toast.error(apiError(e, "Could not load the Add Lead form.")))
      .finally(() => setLoading(false));
  }, [open]);

  const reset = () => { setResult(null); setValues({}); };
  const close = () => { reset(); onClose?.(); };
  const openPerson = (pid) => { close(); navigate(`/people/detail/${pid}`); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form) return;
    const missing = form.fields.find((f) => f.required && f.field_type !== "section" && f.field_type !== "display" && !String(values[f.id] ?? "").trim());
    if (missing) { toast.error(`${missing.label} is required.`); return; }
    setBusy(true);
    try {
      const payload = {};
      form.fields.forEach((f) => { if (f.mapping && LEAD_MAP[f.mapping]) payload[LEAD_MAP[f.mapping]] = values[f.id]; });
      const { data } = await api.post("/referral-partner/leads", payload);
      setResult(data);
      if (data.status === "created") { toast.success("Lead added"); onCreated?.(data); }
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const created = result?.status === "created";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="bg-white max-w-lg max-h-[88vh] overflow-y-auto" data-testid="add-lead-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-brand" />{form?.name || "Add Lead"}</DialogTitle>
          <DialogDescription>{form?.description || "We check for an existing record before creating anyone new."}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-12 text-center text-slate-400" data-testid="add-lead-dialog-loading"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : result ? (
          <div className="py-2 text-center" data-testid="add-lead-dialog-result">
            {created ? (
              <>
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                <p className="font-display font-bold text-slate-900" data-testid="add-lead-dialog-created">Lead Added</p>
                <p className="text-slate-600 mt-0.5">{result.name}</p>
                <div className="flex justify-center gap-2 mt-4">
                  {result.person_id && <Button variant="outline" onClick={() => openPerson(result.person_id)} data-testid="add-lead-dialog-open">Open Record</Button>}
                  <Button className="bg-brand hover:bg-brand-strong text-white" onClick={reset} data-testid="add-lead-dialog-another"><UserPlus className="w-4 h-4 mr-1.5" />Add Another</Button>
                </div>
              </>
            ) : (
              <>
                <Info className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                <p className="font-display font-bold text-slate-900">Already in Top-Recruit</p>
                <p className="text-sm text-slate-600 mt-1" data-testid="add-lead-dialog-existing">{result.message}</p>
                <div className="flex justify-center gap-2 mt-4">
                  {result.allow_open && result.person_id && <Button variant="outline" onClick={() => openPerson(result.person_id)} data-testid="add-lead-dialog-open-existing"><ExternalLink className="w-4 h-4 mr-1.5" />Open {result.name || "Person"}</Button>}
                  <Button className="bg-brand hover:bg-brand-strong text-white" onClick={reset} data-testid="add-lead-dialog-another">Add Another</Button>
                </div>
              </>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            {form && <FormRenderer fields={form.fields} values={values} onChange={setValues} dense />}
            <p className="text-xs text-slate-400">We'll check for an existing record before creating anyone new.</p>
            <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white">
              <Button type="button" variant="outline" onClick={close} data-testid="add-lead-dialog-cancel">Cancel</Button>
              <Button type="submit" disabled={busy || !form} className="bg-brand hover:bg-brand-strong text-white" data-testid="add-lead-dialog-submit"><UserPlus className="w-4 h-4 mr-1.5" />{busy ? "Adding…" : "Add Lead"}</Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
